import { useState, useEffect } from "react";
import { Modal, Button, Group, Stack, Text, Select } from "@mantine/core";
import { IconFileText, IconMail } from "@tabler/icons-react";
import { MentionTextarea } from "./CustomersTable/MentionTextarea";
import { CustomerNoteAttachmentPicker } from "./CustomersTable/CustomerNoteAttachmentPicker";
import {
  useAddCustomerNoteMutation,
  useUpdateCustomerSettingsMutation,
} from "../api/queries/customerRepresentativeQueries";
import { languageOptions } from "../utils/consts";
import { toast } from "sonner";
import type { ThreadOption } from "../api/queries/paymentsQueries";

interface SendActionModalProps {
  opened: boolean;
  action: "updateDescription" | null;
  onClose: () => void;
  invoiceId: string;
  customerRepId?: number;
  currentLanguage?: string;
  onSend: () => Promise<void>;
  threads?: ThreadOption[];
  initialView?: "pickThread" | "updateDescription" | null;
  onSendAsReplyToThread?: (threadId: string) => Promise<void>;
}

export const SendActionModal = ({
  opened,
  action,
  onClose,
  invoiceId,
  customerRepId = 0,
  currentLanguage,
  onSend,
  threads = [],
  initialView = null,
  onSendAsReplyToThread,
}: SendActionModalProps) => {
  const [noteValue, setNoteValue] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [languageValue, setLanguageValue] = useState<string>("English");
  const [view, setView] = useState<"pickThread" | "updateDescription">(
    "pickThread",
  );
  const [pickedThreadId, setPickedThreadId] = useState<string | null>(null);

  const addNoteMutation = useAddCustomerNoteMutation();
  const updateSettingsMutation = useUpdateCustomerSettingsMutation();

  useEffect(() => {
    if (opened) {
      const nextView =
        initialView ??
        (action === "updateDescription" ? "updateDescription" : "pickThread");
      setView(nextView);
    }
  }, [opened, action, initialView]);

  useEffect(() => {
    if (
      opened &&
      (action === "updateDescription" || view === "updateDescription")
    ) {
      setNoteValue("");
      setSelectedFiles([]);
      setLanguageValue(currentLanguage || "English");
    }
  }, [opened, action, currentLanguage, view]);

  const handleSend = async () => {
    try {
      const trimmedNote = noteValue.trim();
      const hasNoteContent = Boolean(trimmedNote) || selectedFiles.length > 0;
      if (hasNoteContent && customerRepId) {
        await addNoteMutation.mutateAsync({
          customerRepId,
          body: trimmedNote || undefined,
          files: selectedFiles,
        });
      }
      if (customerRepId && languageValue !== (currentLanguage || "English")) {
        await updateSettingsMutation.mutateAsync({
          customerRepId,
          language: languageValue || "English",
        });
      }

      try {
        await onSend();
        handleClose();
      } catch (emailError) {
        console.error("Failed to send email after action", emailError);
      }
    } catch (error) {
      console.error("Failed to save note", error);
      toast.error("Failed to save note");
    }
  };

  const handleClose = () => {
    setView("pickThread");
    setPickedThreadId(null);
    onClose();
    setNoteValue("");
    setSelectedFiles([]);
    setLanguageValue(currentLanguage || "English");
  };

  const isLoading =
    addNoteMutation.isPending || updateSettingsMutation.isPending;

  const getTitle = () => {
    if (view === "pickThread") {
      return (
        <Text size="lg" fw={600}>
          Choose a thread to reply to
        </Text>
      );
    }
    if (action === "updateDescription" || view === "updateDescription") {
      return (
        <Group gap="xs">
          <IconFileText size={20} />
          <Text size="lg" fw={600}>
            Add note & send
          </Text>
        </Group>
      );
    }
    return null;
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={getTitle()}
      radius="md"
      padding="xl"
      styles={{
        content: {
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Stack gap="md">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-inner">
          <Group justify="space-between">
            <Text size="sm" fw={500} c="dimmed">
              Invoice
            </Text>
            <Text size="sm" fw={600}>
              {invoiceId}
            </Text>
          </Group>
        </div>

        {view === "pickThread" && (
          <Stack gap="md">
            <button
              type="button"
              className="self-start text-xs text-slate-600 underline"
              onClick={handleClose}
            >
              Cancel
            </button>
            <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
              {threads.map((thread) => {
                const subject =
                  thread.subject.length > 55
                    ? `${thread.subject.slice(0, 52)}...`
                    : thread.subject;
                const isSelected = pickedThreadId === thread.thread_id;
                return (
                  <button
                    key={thread.thread_id}
                    type="button"
                    onClick={() => setPickedThreadId(thread.thread_id)}
                    className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                      isSelected
                        ? "border-blue-500 bg-blue-50 text-blue-900"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {subject}
                  </button>
                );
              })}
            </div>
            <Button
              variant="filled"
              color="blue"
              radius="md"
              leftSection={<IconMail size={18} />}
              disabled={!pickedThreadId}
              onClick={async () => {
                if (!pickedThreadId || !onSendAsReplyToThread) return;
                try {
                  await onSendAsReplyToThread(pickedThreadId);
                  handleClose();
                } catch (e) {
                  console.error("Send as reply to thread failed", e);
                }
              }}
            >
              Reply to this thread
            </Button>
          </Stack>
        )}

        {(action === "updateDescription" || view === "updateDescription") && (
          <>
            <div>
              <Text size="sm" fw={500} mb={8}>
                Note (optional)
              </Text>
              <MentionTextarea
                placeholder="Add a note about this customer before sending... Type @ to mention a teammate."
                value={noteValue}
                onChange={setNoteValue}
                rows={4}
                className="w-full resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              />
              <CustomerNoteAttachmentPicker
                files={selectedFiles}
                onChange={setSelectedFiles}
                disabled={isLoading}
              />
            </div>

            <Select
              label="Language"
              placeholder="Select language"
              value={languageValue}
              onChange={(value) => setLanguageValue(value || "English")}
              data={languageOptions}
              radius="md"
              styles={{
                label: {
                  fontWeight: 500,
                  marginBottom: 8,
                },
              }}
            />

            <Group justify="flex-end" mt="lg">
              <Button
                variant="subtle"
                color="gray"
                radius="md"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                loading={isLoading}
                color="blue"
                radius="md"
                leftSection={<IconFileText size={16} />}
              >
                Add note & send
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
};
